import { useCallback, useMemo } from "react";
import { AutocompleteOption } from "../../components/framework/muiAutocomplete";
import { useAppSelector } from "../../framework/customStore";
import {
    ReportOptionsState,
    ReportOptionState,
} from "../../store/reportOptionsSlice";

export const useReportOptionOptions = <T>(
    key: keyof ReportOptionsState,
    selectedIds: string[],
    id: keyof T,
    name: keyof T
): [
    AutocompleteOption[],
    string[],
    (options: AutocompleteOption[]) => string[]
] => {
    const state = useAppSelector(
        (state) => state.reportOptions[key] as ReportOptionState<T>
    );

    const options: AutocompleteOption[] = useMemo(
        () =>
            Object.keys(state.itemsByName ?? {}).map((key) => ({
                id: key,
                title: key,
            })),
        [state?.itemsByName]
    );

    const selected = useMemo(
        () =>
            selectedIds
                ?.map((id) => state?.itemById?.[id]?.[name] as string)
                .filter(Boolean) ?? [],
        [state?.itemById, selectedIds]
    );

    const getIdsFromOptions = useCallback(
        (options: AutocompleteOption[]) =>
            options?.flatMap(
                (o) =>
                    state?.itemsByName?.[o.id]?.map((i) => i[id] as string) ??
                    []
            ),
        [state?.itemsByName]
    );

    return [options, selected, getIdsFromOptions];
};
